import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Clock, Gem, EyeOff } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Kinetic";
import { SITE, IMG } from "@/data/site";

const VALUES = [
  { icon: ShieldCheck, title: "Professional", desc: "Licensed, insured, impeccably presented chauffeurs." },
  { icon: Clock, title: "Punctual", desc: "Early arrivals and flight-aware scheduling, every time." },
  { icon: Gem, title: "Refined", desc: "Spotless, late-model luxury SUVs with quiet, leather cabins." },
  { icon: EyeOff, title: "Discreet", desc: "Absolute privacy is the default, never the exception." },
];

export default function About() {
  return (
    <main data-testid="page-about" className="pt-20">
      {/* hero */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
        <Reveal>
          <Eyebrow>About Us</Eyebrow>
          <h1 className="mt-6 max-w-3xl font-display text-5xl font-bold leading-[1.05] text-ivory sm:text-6xl">
            Luxury is simply <em className="gold-text italic">getting it right.</em>
          </h1>
          <p className="mt-7 max-w-2xl text-lg font-medium leading-relaxed text-silver">
            Nanak Chauffeurs is a professional ground transportation service
            built on a single idea: that the way you travel should feel as
            considered as where you're going. No surprises, no shortcuts — just
            a reserved car, a professional at the wheel, and a journey engineered
            around you.
          </p>
        </Reveal>
      </section>

      {/* image + story */}
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:px-12">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="gold-frame overflow-hidden rounded-3xl">
              <img
                src={IMG.suvCabin}
                alt="Premium Cadillac Escalade leather cabin interior"
                className="aspect-[4/3] w-full object-cover"
                data-testid="about-image"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl font-semibold leading-tight text-ivory sm:text-4xl">
              A standard you can <em className="gold-text italic">feel.</em>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-silver/85">
              Every ride begins long before the pickup. Routes are planned,
              flights are tracked, and the vehicle is detailed and staged so that
              the moment you step in, there is nothing left to think about.
            </p>
            <p className="mt-5 text-base leading-relaxed text-silver/85">
              Whether it's an airport transfer, a day of back-to-back meetings or
              a quiet evening out, our chauffeurs carry the details so you don't
              have to — with the discretion and polish our clients have come to
              expect.
            </p>
          </Reveal>
        </div>
      </section>

      {/* values */}
      <section className="border-t border-gold/10 bg-obsidian-800/40">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <Eyebrow>What guides us</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-semibold leading-tight text-ivory sm:text-5xl">
              Four promises, kept <em className="gold-text italic">every ride.</em>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 4) * 0.07}>
                <div className="h-full rounded-2xl border border-gold/15 bg-obsidian-700 p-7 transition-colors duration-300 hover:border-gold/40">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-semibold text-ivory">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-silver/80">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* cta */}
      <section className="mx-auto max-w-7xl px-5 py-24 text-center sm:px-8 lg:px-12">
        <Reveal>
          <h2 className="mx-auto max-w-2xl font-display text-4xl font-semibold leading-tight text-ivory sm:text-5xl">
            Ready when you are.
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/reservation"
              data-testid="about-reserve-button"
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-obsidian-900 transition-all duration-300 hover:bg-gold-light"
            >
              Book Your Ride
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="about-email-link"
              className="font-sans text-sm font-medium text-silver transition-colors hover:text-gold"
            >
              {SITE.email}
            </a>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
